import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Crown, Loader2, Mail, Phone, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const money = (value) => `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const BalanceCheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [lookup, setLookup] = useState({ email: '', phone: '' });
  const [bookings, setBookings] = useState([]);
  const [booking, setBooking] = useState(null);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(Boolean(token));
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const minimumPayment = useMemo(() => Number(booking?.minimum_payment || 250), [booking]);
  const balanceDue = Number(booking?.balance_due || 0);
  const totalPrice = Number(booking?.total_price || 0);
  const totalPaid = Number(booking?.total_paid || 0);
  const paidPercentage = totalPrice > 0 ? Math.min((totalPaid / totalPrice) * 100, 100) : 0;
  const ringCircumference = 2 * Math.PI * 48;

  const loadTokenBooking = async (balanceToken) => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API}/payments/balance/${balanceToken}`);
      setBooking(response.data);
      setAmount(String(response.data.minimum_payment || 250));
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'We could not find that balance link. Please use email and phone lookup.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      loadTokenBooking(token);
    }
  }, [token]);

  const handleLookup = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API}/payments/balance/lookup`, lookup);
      setBookings(response.data.bookings || []);
      if ((response.data.bookings || []).length === 1) {
        setBooking(response.data.bookings[0]);
        setAmount(String(response.data.bookings[0].minimum_payment || 250));
      }
    } catch (requestError) {
      setBookings([]);
      setError(requestError.response?.data?.detail || 'No booking was found for those details.');
    } finally {
      setLoading(false);
    }
  };

  const selectBooking = (selected) => {
    setBooking(selected);
    setAmount(String(selected.minimum_payment || 250));
    setError('');
  };

  const handleCheckout = async () => {
    const numericAmount = Number(amount);
    if (!booking || Number.isNaN(numericAmount)) return;
    setProcessing(true);
    setError('');
    try {
      const response = await axios.post(`${API}/payments/balance/${booking.balance_token}/checkout`, {
        amount: numericAmount,
        currency: 'usd',
        origin_url: window.location.origin,
      });
      window.location.href = response.data.url;
    } catch (requestError) {
      const detail = requestError.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'We could not start balance checkout. Please try again or contact King.');
      setProcessing(false);
    }
  };

  return (
    <main className="balance-page" data-testid="balance-checkout-page">
      <section className="balance-hero">
        <Link to="/" className="balance-brand" data-testid="balance-home-link">
          <img src="/king-siege-logo-new.jpeg" alt="King Siege LLC" />
          <span>King Siege LLC</span>
        </Link>
        <p className="eyebrow">Secure guest payments</p>
        <h1>Pay Your Playcation Balance</h1>
        <p>Use your private email link or find your booking with the email and phone number used at checkout.</p>
      </section>

      <section className="balance-layout">
        <Card className="balance-card" data-testid="balance-lookup-card">
          <CardContent className="p-6">
            <div className="balance-card-title"><ShieldCheck /> Find your booking</div>
            <form onSubmit={handleLookup} className="balance-form">
              <div>
                <Label htmlFor="balance-email">Booking email</Label>
                <Input
                  id="balance-email"
                  data-testid="balance-email-input"
                  type="email"
                  value={lookup.email}
                  onChange={(event) => setLookup({ ...lookup, email: event.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="balance-phone">Booking phone</Label>
                <Input
                  id="balance-phone"
                  data-testid="balance-phone-input"
                  type="tel"
                  value={lookup.phone}
                  onChange={(event) => setLookup({ ...lookup, phone: event.target.value })}
                  required
                />
              </div>
              <Button className="gold-cta" data-testid="balance-lookup-button" disabled={loading}>
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5" />}
                Find my balance
              </Button>
            </form>

            {bookings.length > 1 && (
              <div className="balance-booking-list" data-testid="balance-booking-list">
                {bookings.map((item) => (
                  <button type="button" key={item.transaction_id} onClick={() => selectBooking(item)} data-testid={`balance-booking-${item.transaction_id}`}>
                    <span>{item.accommodation_type} · {item.guest_count} guests</span>
                    <strong>{money(item.balance_due)} due</strong>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="balance-card balance-summary-card" data-testid="balance-summary-card">
          <CardContent className="p-6">
            {!booking && !loading && (
              <div className="balance-empty">
                <Crown className="h-10 w-10" />
                <h2>Your balance summary will appear here.</h2>
                <p>Open your private email link or look up your booking to continue.</p>
              </div>
            )}
            {loading && <div className="balance-empty"><Loader2 className="h-10 w-10 animate-spin" /><p>Loading booking...</p></div>}
            {booking && (
              <div className="balance-summary" data-testid="balance-booking-summary">
                <p className="eyebrow">Booking found</p>
                <h2>{booking.guest_name}</h2>
                <div className="balance-detail-grid">
                  <div><span>Package</span><strong>{booking.accommodation_type}</strong></div>
                  <div><span>Guests</span><strong>{booking.guest_count}</strong></div>
                  <div><span>Total</span><strong>{money(booking.total_price)}</strong></div>
                  <div><span>Paid</span><strong>{money(booking.total_paid)}</strong></div>
                </div>
                <div className="balance-due-panel">
                  <div className="balance-progress-layout">
                    <div className="balance-progress-ring" data-testid="balance-progress-ring" aria-label={`${paidPercentage.toFixed(0)} percent paid`}>
                      <svg viewBox="0 0 120 120" role="img" aria-hidden="true">
                        <circle className="balance-progress-track" cx="60" cy="60" r="48" />
                        <circle
                          className="balance-progress-value"
                          cx="60"
                          cy="60"
                          r="48"
                          strokeDasharray={ringCircumference}
                          strokeDashoffset={ringCircumference * (1 - paidPercentage / 100)}
                        />
                      </svg>
                      <div>
                        <strong data-testid="balance-progress-percentage">{paidPercentage.toFixed(0)}%</strong>
                        <span>paid</span>
                      </div>
                    </div>
                    <div className="balance-due-copy">
                      <span>Remaining balance</span>
                      <strong data-testid="balance-due-amount">{money(booking.balance_due)}</strong>
                      <small>Final payment deadline: June 16, 2027</small>
                    </div>
                  </div>
                </div>

                {booking.checkout_available ? (
                  <div className="balance-payment-panel">
                    <Label htmlFor="balance-amount">Payment amount</Label>
                    <Input
                      id="balance-amount"
                      data-testid="balance-amount-input"
                      type="number"
                      min={minimumPayment}
                      max={balanceDue}
                      step="0.01"
                      value={amount}
                      onChange={(event) => setAmount(event.target.value)}
                    />
                    <div className="balance-quick-actions">
                      <button type="button" data-testid="balance-minimum-button" onClick={() => setAmount(String(minimumPayment))}>Minimum {money(minimumPayment)}</button>
                      <button type="button" data-testid="balance-half-button" onClick={() => setAmount(String(Math.min(balanceDue, Math.max(minimumPayment, balanceDue / 2)).toFixed(2)))}>Half balance</button>
                      <button type="button" data-testid="balance-full-button" onClick={() => setAmount(String(balanceDue.toFixed(2)))}>Pay full balance</button>
                    </div>
                    <Button className="gold-cta balance-pay-button" data-testid="balance-pay-button" onClick={handleCheckout} disabled={processing}>
                      {processing ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5" />}
                      {processing ? 'Starting checkout...' : `Pay ${money(Number(amount || 0))}`}
                    </Button>
                  </div>
                ) : (
                  <div className="balance-closed-panel" data-testid="balance-contact-king-panel">
                    <h3>Contact King</h3>
                    <p>{booking.deadline_passed ? 'The final payment deadline has passed.' : 'Balance checkout is not available yet.'}</p>
                    <div>
                      <a href="sms:8325965177" data-testid="balance-text-king-link"><Phone className="h-4 w-4" /> Text King</a>
                      <a href="mailto:siegeplaycations@gmail.com" data-testid="balance-email-king-link"><Mail className="h-4 w-4" /> Email us</a>
                    </div>
                  </div>
                )}
              </div>
            )}
            {error && <div className="balance-error" data-testid="balance-error-alert">{error}</div>}
          </CardContent>
        </Card>
      </section>
    </main>
  );
};

export default BalanceCheckoutPage;
